import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { snapshotSpecBunja } from '@flipster/features/src/shared/snapshot/state';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import Num from '@flipster/utils/num';

import { marginTypeBunja, symbolBunja } from './base';

export const stakedBalanceBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { walletsAtom } = bunja.use(walletsBunja);
  const { symbolInfoAtom } = bunja.use(symbolBunja);
  const { marginTypeAtom } = bunja.use(marginTypeBunja);
  const { currenciesKeyByCurrencyAtom } = bunja.use(snapshotSpecBunja);
  const { userAtom } = bunja.use(authBunja);

  const hasStakedBalanceAtom = atom((get) => {
    const wallets = get(walletsAtom);
    const symbolInfo = get(symbolInfoAtom);
    const marginType = get(marginTypeAtom);
    const currenciesKeyByCurrency = get(currenciesKeyByCurrencyAtom);

    if (!wallets || !symbolInfo || !currenciesKeyByCurrency) return false;

    if (marginType === 'Isolated') {
      const quoteCurrency = symbolInfo.quoteCurrency;
      const wallet = wallets[quoteCurrency];

      if (!wallet) return false;

      return Num(wallet.walletBalanceStakedInUsdt).isGreaterThan(0);
    } else {
      const collateralCurrencies = Object.values(currenciesKeyByCurrency).filter(
        (currency) => currency.collateralAvailable,
      );

      return collateralCurrencies.some((currency) => {
        const wallet = wallets[currency.currency];
        if (!wallet) return false;
        return Num(wallet.walletBalanceStakedInUsdt).isGreaterThan(0);
      });
    }
  });

  const canSkipStakedBalanceWarningBaseAtom = atomWithDefault((get) => {
    const defaultValue = false;
    const user = get(userAtom);
    if (!user) return defaultValue;
    const storageValue = getLocalStorage(getFlipsterConfigsV1Key(user.userId, 'skipStakedBalanceWarning'));
    return typeof storageValue === 'boolean' ? storageValue : defaultValue;
  });

  const canSkipStakedBalanceWarningAtom = atom(
    (get) => get(canSkipStakedBalanceWarningBaseAtom),
    (get, set, value: boolean) => {
      const user = get(userAtom);
      if (!user) return;
      const storageKey = getFlipsterConfigsV1Key(user.userId, 'skipStakedBalanceWarning');
      setLocalStorage(storageKey, value);
      set(canSkipStakedBalanceWarningBaseAtom, value);
    },
  );

  const setSkipStakedBalanceWarning = (value: boolean) => {
    store.set(canSkipStakedBalanceWarningAtom, value);
  };

  return {
    hasStakedBalanceAtom,
    canSkipStakedBalanceWarningAtom,
    setSkipStakedBalanceWarning,
  };
});
stakedBalanceBunja.debugLabel = 'stakedBalanceBunja';
